import { useState, useEffect, useRef, useCallback } from "react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import {
  ArrowRight, Star, Shield, Clock, CheckCircle2, ChevronDown, ChevronUp,
  Phone, Mail, MapPin, Send, Loader2, Heart, Zap, Award, Users, Target,
  Quote, X, Menu as MenuIcon
} from "lucide-react";
import axios from "axios";
import { ShopBlock } from "./ShopBlock";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ==================== COLOR PALETTES ====================
const PALETTES = {
  gesundheit: {
    primary: "bg-cyan-600", primaryHover: "hover:bg-cyan-700", primaryText: "text-cyan-600",
    primaryLight: "bg-cyan-50", primaryBorder: "border-cyan-200",
    gradient: "from-cyan-600 to-teal-500", gradientLight: "from-cyan-50 to-teal-50",
    hero: "from-cyan-900 via-teal-800 to-cyan-900", accent: "bg-teal-500",
    ring: "ring-cyan-500/20",
  },
  handwerk: {
    primary: "bg-orange-600", primaryHover: "hover:bg-orange-700", primaryText: "text-orange-600",
    primaryLight: "bg-orange-50", primaryBorder: "border-orange-200",
    gradient: "from-orange-600 to-amber-500", gradientLight: "from-orange-50 to-amber-50",
    hero: "from-orange-900 via-amber-800 to-orange-900", accent: "bg-amber-500",
    ring: "ring-orange-500/20",
  },
  beauty: {
    primary: "bg-fuchsia-600", primaryHover: "hover:bg-fuchsia-700", primaryText: "text-fuchsia-600",
    primaryLight: "bg-fuchsia-50", primaryBorder: "border-fuchsia-200",
    gradient: "from-fuchsia-600 to-pink-500", gradientLight: "from-fuchsia-50 to-pink-50",
    hero: "from-fuchsia-900 via-pink-900 to-fuchsia-900", accent: "bg-pink-500",
    ring: "ring-fuchsia-500/20",
  },
  gebaeude: {
    primary: "bg-green-600", primaryHover: "hover:bg-green-700", primaryText: "text-green-600",
    primaryLight: "bg-green-50", primaryBorder: "border-green-200",
    gradient: "from-green-600 to-emerald-500", gradientLight: "from-green-50 to-emerald-50",
    hero: "from-green-900 via-emerald-800 to-green-900", accent: "bg-emerald-500",
    ring: "ring-green-500/20",
  },
  gastro: {
    primary: "bg-rose-600", primaryHover: "hover:bg-rose-700", primaryText: "text-rose-600",
    primaryLight: "bg-rose-50", primaryBorder: "border-rose-200",
    gradient: "from-rose-600 to-pink-500", gradientLight: "from-rose-50 to-pink-50",
    hero: "from-rose-900 via-pink-800 to-rose-900", accent: "bg-pink-500",
    ring: "ring-rose-500/20",
  },
  recht: {
    primary: "bg-slate-700", primaryHover: "hover:bg-slate-800", primaryText: "text-slate-700",
    primaryLight: "bg-slate-100", primaryBorder: "border-slate-300",
    gradient: "from-slate-800 to-blue-900", gradientLight: "from-slate-50 to-blue-50",
    hero: "from-slate-900 via-blue-900 to-slate-900", accent: "bg-blue-800",
    ring: "ring-slate-500/20",
  },
  fitness: {
    primary: "bg-emerald-600", primaryHover: "hover:bg-emerald-700", primaryText: "text-emerald-600",
    primaryLight: "bg-emerald-50", primaryBorder: "border-emerald-200",
    gradient: "from-emerald-600 to-green-500", gradientLight: "from-emerald-50 to-green-50",
    hero: "from-emerald-900 via-green-800 to-emerald-900", accent: "bg-green-500",
    ring: "ring-emerald-500/20",
  },
  coaching: {
    primary: "bg-indigo-600", primaryHover: "hover:bg-indigo-700", primaryText: "text-indigo-600",
    primaryLight: "bg-indigo-50", primaryBorder: "border-indigo-200",
    gradient: "from-indigo-600 to-violet-500", gradientLight: "from-indigo-50 to-violet-50",
    hero: "from-indigo-900 via-violet-900 to-indigo-900", accent: "bg-violet-500",
    ring: "ring-indigo-500/20",
  },
  kreativ: {
    primary: "bg-fuchsia-600", primaryHover: "hover:bg-fuchsia-700", primaryText: "text-fuchsia-600",
    primaryLight: "bg-fuchsia-50", primaryBorder: "border-fuchsia-200",
    gradient: "from-fuchsia-600 to-purple-500", gradientLight: "from-fuchsia-50 to-purple-50",
    hero: "from-fuchsia-900 via-purple-900 to-fuchsia-900", accent: "bg-purple-500",
    ring: "ring-fuchsia-500/20",
  },
  standard: {
    primary: "bg-blue-600", primaryHover: "hover:bg-blue-700", primaryText: "text-blue-600",
    primaryLight: "bg-blue-50", primaryBorder: "border-blue-200",
    gradient: "from-blue-600 to-indigo-500", gradientLight: "from-blue-50 to-indigo-50",
    hero: "from-blue-900 via-indigo-900 to-blue-900", accent: "bg-indigo-500",
    ring: "ring-blue-500/20",
  },
};

// ==================== SCROLL ANIMATION HOOK ====================
function useScrollAnim() {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setVisible(true); obs.disconnect(); } }, { threshold: 0.15 });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return [ref, visible];
}

const Anim = ({ children, delay = 0, className = "" }) => {
  const [ref, vis] = useScrollAnim();
  return (
    <div ref={ref} className={`transition-all duration-700 ${vis ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"} ${className}`} style={{ transitionDelay: `${delay}ms` }}>
      {children}
    </div>
  );
};

// ==================== ANIMATED COUNTER ====================
const Counter = ({ value, suffix = "" }) => {
  const [count, setCount] = useState(0);
  const [ref, vis] = useScrollAnim();
  const num = parseInt(value?.toString().replace(/[^0-9]/g, "")) || 0;
  useEffect(() => {
    if (!vis || num === 0) return;
    let start = 0;
    const step = Math.max(1, Math.floor(num / 40));
    const timer = setInterval(() => {
      start += step;
      if (start >= num) { setCount(num); clearInterval(timer); }
      else setCount(start);
    }, 30);
    return () => clearInterval(timer);
  }, [vis, num]);
  const display = value?.toString().includes("+") ? `${count}+` : value?.toString().includes(".") ? (count / 10).toFixed(1) : `${count}`;
  return <span ref={ref}>{vis ? display : "0"}{suffix}</span>;
};

// ==================== MAIN COMPONENT ====================
const GeneratedPageContent = ({ page, content: contentProp, pageData: pageDataProp, isPreview = false }) => {
  // Support both: page={page} (from PreviewPage) or content={} pageData={} (direct)
  const content = contentProp || page?.content || {};
  const pageData = pageDataProp || page || {};

  const [expandedFaq, setExpandedFaq] = useState(null);
  const [formData, setFormData] = useState({ name: "", contact: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [legalModal, setLegalModal] = useState(null);
  const [legalDocs, setLegalDocs] = useState(null);
  const [legalError, setLegalError] = useState(false);
  const [mapConsent, setMapConsent] = useState(false);
  const [mobileMenu, setMobileMenu] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const rootRef = useRef(null);

  const company_name = pageData.company_name || "Unternehmen";
  const industry = pageData.industry || "";
  const city = pageData.city || "";
  const street = pageData.street || "";
  const zip_code = pageData.zip_code || "";
  const contact_type = pageData.contact_type || "email";
  const contact_value = pageData.contact_value || "";
  const pageId = pageData._id || pageData.id || "";

  const category = content?.industry_category || "standard";
  const c = PALETTES[category] || PALETTES.standard;
  const benefits = content?.benefits || [];
  const steps = content?.steps || [];
  const faq = content?.faq || [];
  const services = content?.services || [];
  const testimonials = content?.testimonials || [];
  const googleReviews = content?.google_reviews?.rating ? content.google_reviews : null;
  const reviewsNavId = testimonials.length > 0 ? "testimonials" : "google-reviews";
  const hasReviews = testimonials.length > 0 || !!googleReviews;
  const stats = content?.stats || [];
  const trustElements = content?.trust_elements || [];
  const ctaVariants = content?.cta_variants || ["Jetzt anfragen"];
  const showMap = content?.show_map !== false;
  const images = content?.images || {};
  const heroImage = images.hero || content?.hero_image || "";
  const serviceImages = images.services || [];
  const aboutImage = images.about || content?.trust_image || "";
  const galleryImages = images.gallery || [];
  const benefitSlot = serviceImages[0] ? "services:0" : aboutImage ? "about" : "hero";
  const aboutSlot = aboutImage ? "about" : "hero";
  const bannerSlot = galleryImages[0] ? "gallery:0" : serviceImages[1] ? "services:1" : "hero";

  const fullAddress = `${street}, ${zip_code} ${city}`;
  const mapQuery = encodeURIComponent(fullAddress);

  // Scroll detection - works both in preview container and standalone
  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    const scrollParent = root.closest('[style*="overflow"], .overflow-auto, .overflow-y-auto') || window;
    const handleScroll = () => {
      const scrollTop = scrollParent === window ? window.scrollY : scrollParent.scrollTop;
      setScrolled(scrollTop > 60);
    };
    scrollParent.addEventListener("scroll", handleScroll);
    return () => scrollParent.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    if (isPreview || !pageData.logo_url) return undefined;
    const existing = document.querySelector("link[rel~='icon']");
    const link = existing || document.createElement("link");
    const prevHref = existing ? existing.getAttribute("href") : null;
    const prevType = existing ? existing.getAttribute("type") : null;
    link.setAttribute("rel", "icon");
    link.removeAttribute("type");
    link.setAttribute("href", pageData.logo_url);
    if (!existing) document.head.appendChild(link);
    return () => {
      if (!existing) { link.remove(); return; }
      link.setAttribute("href", prevHref);
      if (prevType) link.setAttribute("type", prevType);
    };
  }, [isPreview, pageData.logo_url]);

  useEffect(() => {
    if (!legalModal || legalDocs || !pageId) return;
    setLegalError(false);
    axios.get(`${API}/pages/${pageId}/legal`).then((r) => setLegalDocs(r.data)).catch(() => setLegalError(true));
  }, [legalModal, legalDocs, pageId]);

  const scrollToContact = useCallback(() => {
    const el = document.getElementById("contact-form");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    setMobileMenu(false);
  }, []);

  const scrollToSection = useCallback((id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    setMobileMenu(false);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isPreview || !formData.name || !formData.contact) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/leads`, { ...formData, page_id: pageId });
      setSubmitted(true);
    } catch { /* silent */ }
    setSubmitting(false);
  };

  // ==================== HEADER ====================
  const PageHeader = () => (
    <header className={`sticky top-0 left-0 right-0 z-50 transition-all duration-300 bg-white/95 backdrop-blur-md shadow-sm`} data-testid="page-header">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 sm:h-20">
          {pageData.logo_url ? (
            <img src={pageData.logo_url} alt={company_name} className="h-9 sm:h-10 w-auto max-w-[180px] object-contain" data-testid="site-logo" />
          ) : (
            <span className="text-lg sm:text-xl font-bold tracking-tight text-slate-900">{company_name}</span>
          )}
          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center gap-6 lg:gap-8">
            {[
              { label: "Leistungen", id: "services" },
              { label: "Über uns", id: "about" },
              ...(hasReviews ? [{ label: "Bewertungen", id: reviewsNavId }] : []),
              { label: "FAQ", id: "faq" },
            ].map((item) => (
              <button key={item.id} onClick={() => scrollToSection(item.id)} className="text-sm font-medium text-slate-600 hover:text-slate-900 transition-colors" data-testid={`nav-${item.id}`}>
                {item.label}
              </button>
            ))}
            <Button onClick={scrollToContact} className={`h-10 px-6 rounded-full text-sm font-semibold ${c.primary} ${c.primaryHover} text-white`} data-testid="nav-cta">
              {ctaVariants[0] || "Kontakt"}
            </Button>
          </nav>
          {/* Mobile Menu Button */}
          <button onClick={() => setMobileMenu(!mobileMenu)} className="md:hidden p-2 rounded-lg text-slate-700 transition-colors" data-testid="mobile-menu-toggle">
            {mobileMenu ? <X className="w-6 h-6" /> : <MenuIcon className="w-6 h-6" />}
          </button>
        </div>
      </div>
      {/* Mobile Nav */}
      {mobileMenu && (
        <div className="md:hidden bg-white border-t shadow-lg animate-in slide-in-from-top-2">
          <div className="px-4 py-3 space-y-1">
            {["Leistungen|services", "Über uns|about", ...(hasReviews ? [`Bewertungen|${reviewsNavId}`] : []), "FAQ|faq"].map((item) => {
              const [label, id] = item.split("|");
              return <button key={id} onClick={() => scrollToSection(id)} className="block w-full text-left py-3 text-slate-700 font-medium border-b border-slate-100">{label}</button>;
            })}
            <Button onClick={scrollToContact} className={`w-full h-12 mt-2 rounded-full ${c.primary} ${c.primaryHover} text-white`}>{ctaVariants[0]}</Button>
          </div>
        </div>
      )}
    </header>
  );

  // ==================== HERO ====================
  const Hero = () => (
    <section className="relative min-h-[85vh] flex items-center overflow-hidden" data-testid="hero-section">
      {/* Background Image + Gradient Overlay */}
      <div className="absolute inset-0">
        <img src={heroImage} data-img-slot="hero" alt={company_name} className="w-full h-full object-cover" loading="eager" />
        <div className={`absolute inset-0 bg-gradient-to-br ${c.hero} opacity-85`} />
        <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-black/20" />
      </div>
      {/* Decorative elements */}
      <div className="absolute top-20 right-10 w-72 h-72 bg-white/5 rounded-full blur-3xl" />
      <div className="absolute bottom-20 left-10 w-96 h-96 bg-white/5 rounded-full blur-3xl" />
      {/* Content */}
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32 sm:py-40 w-full">
        <div className="max-w-3xl">
          <Anim>
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full text-white/90 text-sm font-medium mb-6 border border-white/20">
              <MapPin className="w-4 h-4" />
              <span>{industry} in {city}</span>
            </div>
          </Anim>
          <Anim delay={100}>
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-extrabold text-white tracking-tight leading-[1.1] mb-6">
              {content?.headline}
            </h1>
          </Anim>
          <Anim delay={200}>
            <p className="text-lg sm:text-xl text-white/85 mb-8 max-w-2xl leading-relaxed">
              {content?.subheadline}
            </p>
          </Anim>
          <Anim delay={300}>
            <div className="flex flex-col sm:flex-row gap-4">
              <Button onClick={scrollToContact} className="h-14 px-8 rounded-full text-lg font-semibold bg-white text-slate-900 hover:bg-slate-100 shadow-xl shadow-black/20">
                {content?.cta_text || "Jetzt anfragen"}
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
              <Button onClick={() => scrollToSection("services")} variant="outline" className="h-14 px-8 rounded-full text-lg font-semibold text-white border-white/30 hover:bg-white/10 backdrop-blur-sm">
                Unsere Leistungen
              </Button>
            </div>
          </Anim>
          {/* Trust badges in hero */}
          {trustElements.length > 0 && (
            <Anim delay={500}>
              <div className="flex flex-wrap gap-4 mt-10">
                {trustElements.slice(0, 3).map((t, i) => (
                  <div key={i} className="flex items-center gap-2 text-white/80 text-sm">
                    <CheckCircle2 className="w-4 h-4 text-green-400" />
                    <span>{t}</span>
                  </div>
                ))}
              </div>
            </Anim>
          )}
        </div>
      </div>
    </section>
  );

  // ==================== STATS BANNER ====================
  const StatsBanner = () => stats.length > 0 && (
    <section className={`py-10 sm:py-14 bg-gradient-to-r ${c.gradient} text-white`} data-testid="stats-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
          {stats.slice(0, 4).map((s, i) => (
            <Anim key={i} delay={i * 100}>
              <p className="text-3xl sm:text-4xl lg:text-5xl font-extrabold mb-1">
                <Counter value={s.value} />
              </p>
              <p className="text-sm sm:text-base text-white/80">{s.label}</p>
            </Anim>
          ))}
        </div>
      </div>
    </section>
  );

  // ==================== SERVICES ====================
  const ServicesSection = () => services.length > 0 && (
    <section id="services" className="py-16 sm:py-20 lg:py-28 bg-white" data-testid="services-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Anim>
          <div className="text-center mb-14">
            <span className={`inline-block px-4 py-1.5 rounded-full ${c.primaryLight} ${c.primaryText} text-sm font-semibold mb-4`}>Unsere Leistungen</span>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-900">Was wir für Sie tun</h2>
          </div>
        </Anim>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {services.slice(0, 4).map((svc, i) => {
            // Cycle through available images if we have fewer images than services
            const imgUrl = serviceImages.length > 0 ? serviceImages[i % serviceImages.length] : heroImage;
            const imgSlot = serviceImages.length > 0 ? `services:${i % serviceImages.length}` : "hero";
            
            return (
              <Anim key={i} delay={i * 100}>
                <div className="group relative overflow-hidden rounded-2xl bg-white border border-slate-200/60 hover:shadow-xl hover:-translate-y-1 transition-all duration-300">
                  {imgUrl && (
                    <div className="h-44 overflow-hidden">
                      <img src={imgUrl} data-img-slot={imgSlot} alt={svc.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                      <div className={`absolute top-0 left-0 right-0 h-44 bg-gradient-to-t from-black/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity`} />
                    </div>
                  )}
                  <div className="p-6">
                    <div className={`w-10 h-10 ${c.primaryLight} rounded-xl flex items-center justify-center ${c.primaryText} mb-4 group-hover:scale-110 transition-transform`}>
                      <span className="font-bold text-lg">{i + 1}</span>
                    </div>
                    <h3 className="text-lg font-bold text-slate-900 mb-2">{svc.title}</h3>
                    <p className="text-sm text-slate-600 leading-relaxed">{svc.description}</p>
                  </div>
                </div>
              </Anim>
            );
          })}
        </div>
      </div>
    </section>
  );

  // ==================== BENEFITS ====================
  const BenefitsSection = () => benefits.length > 0 && (
    <section className={`py-16 sm:py-20 lg:py-28 bg-gradient-to-b ${c.gradientLight}`} data-testid="benefits-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <Anim>
            <div>
              <span className={`inline-block px-4 py-1.5 rounded-full ${c.primaryLight} ${c.primaryText} text-sm font-semibold mb-4`}>Ihre Vorteile</span>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-900 mb-4">Warum Kunden uns vertrauen</h2>
              <p className="text-slate-600 mb-8 text-lg">{content?.offer?.description || `Professioneller ${industry}-Service in ${city}`}</p>
              <div className="space-y-4">
                {benefits.slice(0, 4).map((b, i) => (
                  <div key={i} className="flex gap-4 items-start p-4 bg-white rounded-xl shadow-sm border border-slate-100 hover:shadow-md transition-shadow">
                    <div className={`w-10 h-10 ${c.primary} text-white rounded-xl flex items-center justify-center flex-shrink-0`}>
                      <CheckCircle2 className="w-5 h-5" />
                    </div>
                    <div>
                      <h3 className="font-bold text-slate-900">{b.title}</h3>
                      <p className="text-sm text-slate-600 mt-0.5">{b.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </Anim>
          <Anim delay={200}>
            <div className="relative">
              <div className={`absolute -inset-4 bg-gradient-to-br ${c.gradient} rounded-3xl opacity-10 blur-2xl`} />
              <img src={serviceImages[0] || aboutImage || heroImage} data-img-slot={benefitSlot} alt={company_name} className="relative rounded-2xl shadow-2xl w-full h-[400px] sm:h-[480px] object-cover" loading="lazy" />
              {/* Floating badge */}
              {stats.length > 0 && (
                <div className="absolute -bottom-6 -left-6 bg-white rounded-2xl shadow-xl p-4 hidden sm:block">
                  <p className={`text-2xl font-extrabold ${c.primaryText}`}>{stats[0]?.value}</p>
                  <p className="text-xs text-slate-500">{stats[0]?.label}</p>
                </div>
              )}
            </div>
          </Anim>
        </div>
      </div>
    </section>
  );

  // ==================== PROCESS ====================
  const ProcessSection = () => steps.length > 0 && (
    <section className="py-16 sm:py-20 lg:py-28 bg-white" data-testid="process-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Anim>
          <div className="text-center mb-14">
            <span className={`inline-block px-4 py-1.5 rounded-full ${c.primaryLight} ${c.primaryText} text-sm font-semibold mb-4`}>So funktioniert's</span>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-900">In {steps.length} Schritten zum Ziel</h2>
          </div>
        </Anim>
        <div className="grid sm:grid-cols-3 gap-8 relative">
          {/* Connection line */}
          <div className={`hidden sm:block absolute top-12 left-[20%] right-[20%] h-0.5 bg-gradient-to-r ${c.gradient} opacity-20`} />
          {steps.slice(0, 3).map((step, i) => (
            <Anim key={i} delay={i * 150}>
              <div className="text-center relative">
                <div className={`w-20 h-20 mx-auto mb-6 bg-gradient-to-br ${c.gradient} text-white rounded-2xl flex items-center justify-center font-extrabold text-2xl shadow-lg ${c.ring} ring-8`}>
                  {i + 1}
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-2">{step.title || step.step}</h3>
                <p className="text-slate-600">{step.description}</p>
              </div>
            </Anim>
          ))}
        </div>
      </div>
    </section>
  );

  // ==================== ABOUT ====================
  const AboutSection = () => content?.about_text && (
    <section id="about" className="py-16 sm:py-20 lg:py-28 bg-slate-50" data-testid="about-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <Anim>
            <div className="relative order-2 lg:order-1">
              <div className={`absolute -inset-3 bg-gradient-to-tr ${c.gradient} rounded-3xl opacity-10`} />
              <img src={aboutImage || heroImage} data-img-slot={aboutSlot} alt={`Über ${company_name}`} className="relative rounded-2xl shadow-xl w-full h-[380px] sm:h-[440px] object-cover" loading="lazy" />
            </div>
          </Anim>
          <Anim delay={150} className="order-1 lg:order-2">
            <div>
              <span className={`inline-block px-4 py-1.5 rounded-full ${c.primaryLight} ${c.primaryText} text-sm font-semibold mb-4`}>Über uns</span>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-900 mb-6">{company_name}</h2>
              <p className="text-lg text-slate-600 leading-relaxed mb-6">{content.about_text}</p>
              {stats.length >= 2 && (
                <div className="flex items-center gap-6 pt-4 border-t border-slate-200">
                  <div>
                    <p className={`text-2xl font-extrabold ${c.primaryText}`}>{stats[stats.length > 3 ? 3 : 1]?.value}</p>
                    <p className="text-sm text-slate-500">{stats[stats.length > 3 ? 3 : 1]?.label}</p>
                  </div>
                  <div className="w-px h-12 bg-slate-200" />
                  <div>
                    <p className={`text-2xl font-extrabold ${c.primaryText}`}>{stats[0]?.value}</p>
                    <p className="text-sm text-slate-500">{stats[0]?.label}</p>
                  </div>
                </div>
              )}
            </div>
          </Anim>
        </div>
      </div>
    </section>
  );

  // ==================== GOOGLE REVIEWS ====================
  const GoogleReviewsSection = () => googleReviews && (
    <section id="google-reviews" className="py-16 sm:py-20 bg-slate-50" data-testid="google-reviews-section">
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
          <div>
            <h2 className="text-2xl sm:text-3xl font-bold text-slate-900">Bewertungen auf Google</h2>
            <p className="text-slate-600 mt-1" data-testid="google-rating-summary">
              <span className="font-bold text-slate-900">{String(googleReviews.rating).replace(".", ",")}</span> von 5 Sternen · {googleReviews.count} Bewertungen
            </p>
          </div>
          {googleReviews.maps_uri && (
            <a href={googleReviews.maps_uri} target="_blank" rel="noopener noreferrer" className="text-sm font-semibold text-slate-700 underline" data-testid="google-maps-link">Alle Bewertungen auf Google ansehen</a>
          )}
        </div>
        <div className="grid md:grid-cols-3 gap-5">
          {(googleReviews.reviews || []).slice(0, 5).map((r, i) => (
            <div key={i} className="bg-white rounded-2xl border border-slate-100 p-5 shadow-sm" data-testid={`google-review-${i}`}>
              <p className="text-amber-500 text-sm mb-2" aria-label={`${r.rating} von 5 Sternen`}>{"★".repeat(Math.round(r.rating || 0))}<span className="text-slate-300">{"★".repeat(5 - Math.round(r.rating || 0))}</span></p>
              {r.text && <p className="text-slate-700 text-sm leading-relaxed line-clamp-6">{r.text}</p>}
              <p className="text-xs text-slate-500 mt-3">
                {r.author_uri ? <a href={r.author_uri} target="_blank" rel="noopener noreferrer" className="font-semibold underline">{r.author}</a> : <span className="font-semibold">{r.author}</span>}
                {r.when ? ` · ${r.when}` : ""}
                {r.uri && <> · <a href={r.uri} target="_blank" rel="noopener noreferrer" className="underline">auf Google ansehen</a></>}
              </p>
            </div>
          ))}
        </div>
        <p className="flex items-center gap-2 text-xs text-slate-500 mt-6" data-testid="google-attribution">
          <svg viewBox="0 0 48 48" className="w-4 h-4" aria-hidden="true">
            <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
            <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
            <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
            <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
          </svg>
          <span>Bewertungen von <strong className="font-semibold text-slate-600">Google</strong> · Auswahl der von Google bereitgestellten Rezensionen</span>
        </p>
      </div>
    </section>
  );

  // ==================== TESTIMONIALS ====================
  const TestimonialsSection = () => testimonials.length > 0 && (
    <section id="testimonials" className="py-16 sm:py-20 lg:py-28 bg-white" data-testid="testimonials-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Anim>
          <div className="text-center mb-14">
            <span className={`inline-block px-4 py-1.5 rounded-full ${c.primaryLight} ${c.primaryText} text-sm font-semibold mb-4`}>Kundenstimmen</span>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-900">Das sagen unsere Kunden</h2>
            <p className="text-slate-500 mt-2">Echte Bewertungen aus {city} und Umgebung</p>
          </div>
        </Anim>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {testimonials.slice(0, 3).map((t, i) => (
            <Anim key={i} delay={i * 100}>
              <div className="group bg-white rounded-2xl p-6 border border-slate-200/60 hover:shadow-lg hover:-translate-y-1 transition-all duration-300 h-full flex flex-col">
                <div className="flex gap-1 mb-4">
                  {[...Array(t.rating || 5)].map((_, s) => (
                    <Star key={s} className="w-4 h-4 fill-amber-400 text-amber-400" />
                  ))}
                </div>
                <Quote className={`w-8 h-8 ${c.primaryText} opacity-20 mb-2`} />
                <p className="text-slate-700 leading-relaxed flex-1">{t.text}</p>
                <div className="flex items-center gap-3 pt-4 mt-4 border-t border-slate-100">
                  <div className={`w-11 h-11 rounded-full bg-gradient-to-br ${c.gradient} text-white flex items-center justify-center`}>
                    <span className="font-bold text-sm">{t.name?.charAt(0)}</span>
                  </div>
                  <div>
                    <p className="font-semibold text-slate-900 text-sm">{t.name}</p>
                    <p className="text-xs text-slate-500">{t.location}</p>
                  </div>
                </div>
              </div>
            </Anim>
          ))}
        </div>
      </div>
    </section>
  );

  // ==================== GALLERY / EYE-CATCHER ====================
  const GalleryBanner = () => (galleryImages.length > 0 || serviceImages.length > 1) && (
    <section className="py-0 overflow-hidden" data-testid="gallery-section">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-1">
        {[...galleryImages, ...serviceImages].slice(0, 4).map((img, i) => (
          <div key={i} className="relative h-48 sm:h-64 overflow-hidden group">
            <img src={img} data-img-slot={i < galleryImages.length ? `gallery:${i}` : `services:${i - galleryImages.length}`} alt={`${company_name} ${i + 1}`} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" loading="lazy" />
            <div className={`absolute inset-0 bg-gradient-to-t ${c.gradient} opacity-0 group-hover:opacity-30 transition-opacity duration-300`} />
          </div>
        ))}
      </div>
    </section>
  );

  // ==================== FAQ ====================
  const FaqSection = () => faq.length > 0 && (
    <section id="faq" className={`py-16 sm:py-20 lg:py-28 bg-gradient-to-b ${c.gradientLight}`} data-testid="faq-section">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <Anim>
          <div className="text-center mb-12">
            <span className={`inline-block px-4 py-1.5 rounded-full ${c.primaryLight} ${c.primaryText} text-sm font-semibold mb-4`}>FAQ</span>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-900">Häufig gestellte Fragen</h2>
          </div>
        </Anim>
        <div className="space-y-3">
          {faq.map((item, i) => (
            <Anim key={i} delay={i * 50}>
              <div className={`bg-white rounded-xl border overflow-hidden transition-all ${expandedFaq === i ? `${c.primaryBorder} shadow-md` : "border-slate-200"}`}>
                <button onClick={(e) => { e.preventDefault(); setExpandedFaq(expandedFaq === i ? null : i); }} className="w-full px-6 py-5 flex items-center justify-between text-left">
                  <span className={`font-semibold pr-4 ${expandedFaq === i ? "text-slate-900" : "text-slate-700"}`}>{item.question}</span>
                  <ChevronDown className={`w-5 h-5 flex-shrink-0 transition-transform duration-200 ${expandedFaq === i ? "rotate-180" : ""} ${c.primaryText}`} />
                </button>
                <div className={`transition-all duration-300 overflow-hidden ${expandedFaq === i ? "max-h-96" : "max-h-0"}`}>
                  <div className="px-6 pb-5"><p className="text-slate-600 leading-relaxed">{item.answer}</p></div>
                </div>
              </div>
            </Anim>
          ))}
        </div>
      </div>
    </section>
  );

  // ==================== CTA BANNER ====================
  const CtaBanner = () => (
    <section className={`relative py-16 sm:py-20 overflow-hidden`} data-testid="cta-section">
      <div className="absolute inset-0">
        <img src={galleryImages[0] || serviceImages[1] || heroImage} data-img-slot={bannerSlot} alt="" className="w-full h-full object-cover" loading="lazy" />
        <div className={`absolute inset-0 bg-gradient-to-r ${c.hero} opacity-90`} />
      </div>
      <div className="relative max-w-4xl mx-auto px-4 sm:px-6 text-center text-white">
        <Anim>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-4">{content?.final_cta || "Bereit loszulegen?"}</h2>
          <p className="text-lg text-white/80 mb-8 max-w-2xl mx-auto">Kontaktieren Sie {company_name} noch heute – wir freuen uns auf Sie!</p>
          <Button onClick={scrollToContact} className="h-14 px-10 rounded-full text-lg font-semibold bg-white text-slate-900 hover:bg-slate-100 shadow-xl">
            {ctaVariants[0] || "Jetzt anfragen"}
            <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
        </Anim>
      </div>
    </section>
  );

  // ==================== MAP + CONTACT ====================
  const ContactMapSection = () => (
    <section id="contact-form" className="py-16 sm:py-20 lg:py-28 bg-white" data-testid="contact-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Anim>
          <div className="text-center mb-14">
            <span className={`inline-block px-4 py-1.5 rounded-full ${c.primaryLight} ${c.primaryText} text-sm font-semibold mb-4`}>Kontakt</span>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-900">{ctaVariants[0] || "Jetzt anfragen"}</h2>
            <p className="text-slate-500 mt-2">Wir melden uns schnellstmöglich bei Ihnen</p>
          </div>
        </Anim>
        <div className="grid lg:grid-cols-2 gap-10">
          {/* Contact Form */}
          <Anim>
            <div className={`bg-gradient-to-br ${c.gradientLight} rounded-2xl p-6 sm:p-8 border ${c.primaryBorder} border-opacity-30`}>
              {submitted ? (
                <div className="text-center py-12">
                  <div className={`w-16 h-16 mx-auto mb-4 bg-gradient-to-br ${c.gradient} rounded-full flex items-center justify-center text-white`}>
                    <CheckCircle2 className="w-8 h-8" />
                  </div>
                  <h3 className="text-xl font-bold text-slate-900 mb-2">Vielen Dank!</h3>
                  <p className="text-slate-600">Wir melden uns schnellstmöglich bei Ihnen.</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <Label className="text-slate-700 font-semibold">Ihr Name *</Label>
                    <Input value={formData.name} onChange={(e) => setFormData(p => ({ ...p, name: e.target.value }))} placeholder="Max Mustermann" className="h-12 mt-1.5 rounded-xl" disabled={isPreview} data-testid="contact-name-input" />
                  </div>
                  <div>
                    <Label className="text-slate-700 font-semibold">{contact_type === "email" ? "E-Mail *" : "Telefon *"}</Label>
                    <Input type={contact_type === "email" ? "email" : "tel"} value={formData.contact} onChange={(e) => setFormData(p => ({ ...p, contact: e.target.value }))} placeholder={contact_type === "email" ? "ihre@email.de" : "+49 123 456"} className="h-12 mt-1.5 rounded-xl" disabled={isPreview} data-testid="contact-email-input" />
                  </div>
                  <div>
                    <Label className="text-slate-700 font-semibold">Ihre Nachricht</Label>
                    <Textarea value={formData.message} onChange={(e) => setFormData(p => ({ ...p, message: e.target.value }))} placeholder="Wie können wir Ihnen helfen?" rows={4} className="mt-1.5 rounded-xl" disabled={isPreview} data-testid="contact-message-input" />
                  </div>
                  <p className="text-xs text-slate-500 leading-relaxed" data-testid="contact-privacy-note">
                    Ihre Angaben werden zur Bearbeitung Ihrer Anfrage verwendet. Details in der{" "}
                    <button type="button" onClick={() => setLegalModal("datenschutz")} className="underline" data-testid="contact-privacy-link">Datenschutzerklärung</button>.
                  </p>
                  <Button type="submit" disabled={submitting || isPreview} className={`w-full h-12 rounded-xl ${c.primary} ${c.primaryHover} text-white font-semibold text-base`} data-testid="contact-submit-btn">
                    {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : <><Send className="w-4 h-4 mr-2" />{ctaVariants[2] || "Nachricht senden"}</>}
                  </Button>
                </form>
              )}
            </div>
          </Anim>
          {/* Map + Contact Info */}
          <Anim delay={150}>
            <div className="space-y-6">
              {/* Contact Info */}
              <div className="space-y-4">
                <div className="flex items-start gap-4 p-4 bg-slate-50 rounded-xl">
                  <div className={`w-10 h-10 ${c.primary} text-white rounded-xl flex items-center justify-center flex-shrink-0`}>
                    <MapPin className="w-5 h-5" />
                  </div>
                  <div>
                    <p className="font-semibold text-slate-900">Adresse</p>
                    <p className="text-sm text-slate-600">{street && `${street}, `}{zip_code} {city}</p>
                  </div>
                </div>
                <div className="flex items-start gap-4 p-4 bg-slate-50 rounded-xl">
                  <div className={`w-10 h-10 ${c.primary} text-white rounded-xl flex items-center justify-center flex-shrink-0`}>
                    {contact_type === "email" ? <Mail className="w-5 h-5" /> : <Phone className="w-5 h-5" />}
                  </div>
                  <div>
                    <p className="font-semibold text-slate-900">{contact_type === "email" ? "E-Mail" : "Telefon"}</p>
                    <a href={contact_type === "email" ? `mailto:${contact_value}` : `tel:${contact_value}`} className={`text-sm ${c.primaryText} hover:underline`}>{contact_value}</a>
                  </div>
                </div>
              </div>
              {/* Map */}
              {showMap && (street || city) && (
                <div className="rounded-2xl overflow-hidden shadow-md border border-slate-200 h-[280px]" data-testid="map-embed">
                  {mapConsent ? (
                    <iframe
                      title="Standort"
                      width="100%"
                      height="100%"
                      style={{ border: 0 }}
                      loading="lazy"
                      referrerPolicy="no-referrer-when-downgrade"
                      src={`https://maps.google.com/maps?q=${mapQuery}&output=embed&z=15`}
                    />
                  ) : (
                    <div className="h-full flex flex-col items-center justify-center text-center gap-3 bg-slate-100 px-6" data-testid="map-consent-placeholder">
                      <MapPin className="w-8 h-8 text-slate-400" />
                      <p className="text-xs sm:text-sm text-slate-600 max-w-sm leading-relaxed">
                        Mit dem Laden der Karte werden Daten (u. a. Ihre IP-Adresse) an Google übermittelt und ggf. Cookies gesetzt.
                        Mehr dazu in der <button type="button" onClick={() => setLegalModal("datenschutz")} className="underline" data-testid="map-privacy-link">Datenschutzerklärung</button>.
                      </p>
                      <Button type="button" onClick={() => setMapConsent(true)} className={`rounded-full ${c.primary} ${c.primaryHover} text-white`} data-testid="map-load-btn">
                        Karte laden (Einwilligung)
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </Anim>
        </div>
      </div>
    </section>
  );

  // ==================== FOOTER ====================
  const Footer = () => (
    <footer className="bg-slate-900 text-white" data-testid="page-footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8 sm:gap-12">
          <div>
            {pageData.logo_url ? (
              <span className="inline-block bg-white rounded-lg p-2 mb-3"><img src={pageData.logo_url} alt={company_name} className="h-9 w-auto max-w-[160px] object-contain" data-testid="footer-logo" /></span>
            ) : (
              <h3 className="text-xl font-bold mb-3">{company_name}</h3>
            )}
            <p className="text-slate-400 text-sm leading-relaxed">{content?.about_text?.slice(0, 120)}...</p>
          </div>
          <div>
            <h4 className="font-semibold text-white mb-4">Kontakt</h4>
            <div className="space-y-3 text-sm text-slate-400">
              {street && <p className="flex items-center gap-2"><MapPin className="w-4 h-4" />{street}, {zip_code} {city}</p>}
              {contact_value && (
                <a href={contact_type === "email" ? `mailto:${contact_value}` : `tel:${contact_value}`} className="flex items-center gap-2 hover:text-white transition-colors">
                  {contact_type === "email" ? <Mail className="w-4 h-4" /> : <Phone className="w-4 h-4" />}
                  {contact_value}
                </a>
              )}
            </div>
          </div>
          <div>
            <h4 className="font-semibold text-white mb-4">Navigation</h4>
            <div className="space-y-2">
              {["Leistungen|services", "Über uns|about", ...(hasReviews ? [`Bewertungen|${reviewsNavId}`] : []), "FAQ|faq", "Kontakt|contact-form"].map((item) => {
                const [label, id] = item.split("|");
                return <button key={id} onClick={() => scrollToSection(id)} className="block text-sm text-slate-400 hover:text-white transition-colors">{label}</button>;
              })}
            </div>
          </div>
        </div>
      </div>
      <div className="border-t border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs sm:text-sm text-slate-500">
          <div>
            <p>&copy; {new Date().getFullYear()} {company_name}. Alle Rechte vorbehalten.</p>
            {images.credits?.length > 0 && (
              <p className="mt-1 text-xs text-slate-400" data-testid="photo-credits">
                Fotos: {images.credits.map((cr, i) => (
                  <span key={cr.link}>{i > 0 && ", "}<a href={cr.link} target="_blank" rel="noopener noreferrer" className="underline hover:text-white">{cr.name}</a></span>
                ))} auf <a href="https://unsplash.com/?utm_source=webgenerator&utm_medium=referral" target="_blank" rel="noopener noreferrer" className="underline hover:text-white">Unsplash</a>
              </p>
            )}
          </div>
          <div className="flex items-center gap-4">
            <button onClick={() => setLegalModal("impressum")} className="hover:text-white transition-colors" data-testid="footer-impressum-link">Impressum</button>
            <button onClick={() => setLegalModal("datenschutz")} className="hover:text-white transition-colors" data-testid="footer-datenschutz-link">Datenschutz</button>
          </div>
        </div>
      </div>
    </footer>
  );

  // ==================== LEGAL MODAL ====================
  const renderWithPlaceholders = (text) =>
    text.split("BITTE ANGEBEN").flatMap((part, idx, arr) =>
      idx < arr.length - 1
        ? [part, <mark key={idx} className="bg-yellow-200 text-slate-900 px-1 rounded font-semibold" data-testid="legal-missing-marker">BITTE ANGEBEN</mark>]
        : [part]
    );

  const LegalModal = () => {
    if (!legalModal) return null;
    const doc = legalDocs?.[legalModal];
    return (
      <div className="fixed inset-0 bg-black/60 z-[9999] flex items-center justify-center p-4" onClick={() => setLegalModal(null)}>
        <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[85vh] flex flex-col" onClick={(e) => e.stopPropagation()} data-testid={`legal-modal-${legalModal}`}>
          <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
            <h2 className="text-xl font-bold text-slate-900">{legalModal === "impressum" ? "Impressum" : "Datenschutzerklärung"}</h2>
            <button onClick={() => setLegalModal(null)} className="p-1.5 rounded-lg hover:bg-slate-100" data-testid="legal-modal-close"><X className="w-5 h-5 text-slate-500" /></button>
          </div>
          <div className="overflow-y-auto px-6 py-5 space-y-5">
            {!doc && !legalError && <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>}
            {legalError && <p className="text-sm text-red-600" data-testid="legal-error">Die Rechtstexte konnten nicht geladen werden. Bitte versuchen Sie es später erneut.</p>}
            {doc?.sections.map((sec) => (
              <div key={sec.id}>
                <h3 className="text-sm font-bold text-slate-900 mb-1.5">{sec.heading}</h3>
                {sec.paragraphs.map((t, k) => (
                  <p key={k} className="text-sm text-slate-600 whitespace-pre-line leading-relaxed mb-2 break-words">{renderWithPlaceholders(t)}</p>
                ))}
                {sec.items && (
                  <ul className="list-disc pl-5 space-y-1 text-sm text-slate-600">
                    {sec.items.map((t, k) => <li key={k}>{renderWithPlaceholders(t)}</li>)}
                  </ul>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  // ==================== MAIN RENDER ====================
  return (
    <div ref={rootRef} className="min-h-screen bg-white font-sans" style={{ overflowX: "clip" }} data-testid="generated-page">
      {PageHeader()}
      {Hero()}
      {StatsBanner()}
      {ServicesSection()}
      {BenefitsSection()}
      {ProcessSection()}
      {AboutSection()}
      {GalleryBanner()}
      {TestimonialsSection()}
      {GoogleReviewsSection()}
      {FaqSection()}
      {CtaBanner()}
      <ShopBlock pageId={pageId} isPreview={isPreview} buttonClass={`${c.primary} ${c.primaryHover}`} />
      {ContactMapSection()}
      {Footer()}
      {LegalModal()}
    </div>
  );
};

export default GeneratedPageContent;
